import { formatDate } from "utils/funcs"
import type { EvoListFilters } from "hooks/usePersistedFilters"

export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]

export function yearOptions(
  start = 2020,
  end = Math.max(new Date().getFullYear() + 1, 2027)
) {
  const years: number[] = []
  for (let y = end; y >= start; y--) years.push(y)
  return years
}

const ACTIVE_TAB_KEY = "evo-my-trips-active-tab"
const SHARED_NAME_KEY = "evo-my-trips-filters:shared-fullName"
const SHARED_MONTH_KEY = "evo-my-trips-filters:shared-month"
const SHARED_YEAR_KEY = "evo-my-trips-filters:shared-year"

const TAB_SLUGS: Record<string, string> = {
  "My Trips": "my-trips",
  "Travel by Month": "travel-by-month",
  Operations: "operations",
  "Operations by Month": "operations-by-month",
  "Receipt Transactions": "receipt-transactions",
  "Receipt transactions": "receipt-transactions",
}

const SLUG_TO_TAB: Record<string, string> = {
  "my-trips": "My Trips",
  "travel-by-month": "Travel by Month",
  operations: "Operations",
  "operations-by-month": "Operations by Month",
  "receipt-transactions": "Receipt Transactions",
}

export function tabToSlug(tab: string) {
  return TAB_SLUGS[tab] ?? "my-trips"
}

export function tabFromSlug(slug: string | undefined | null) {
  if (!slug) return null
  return SLUG_TO_TAB[String(slug).toLowerCase()] ?? null
}

export function myTripsPathForTab(tab: string) {
  return `/my-trips?tab=${tabToSlug(tab)}`
}

export function myTripsPathWithPersistedTab() {
  return myTripsPathForTab(getPersistedTripsTab())
}

export function getPersistedTripsTab(defaultTab = "My Trips") {
  try {
    const tab = sessionStorage.getItem(ACTIVE_TAB_KEY) || defaultTab
    if (tab === "Receipt transactions") return "Receipt Transactions"
    return tab
  } catch {
    return defaultTab
  }
}

export function setPersistedTripsTab(tab: string) {
  try {
    sessionStorage.setItem(ACTIVE_TAB_KEY, tab)
  } catch {
    /* ignore */
  }
}

export function getSharedFullNameFilter() {
  try {
    return sessionStorage.getItem(SHARED_NAME_KEY) ?? ""
  } catch {
    return ""
  }
}

export function setSharedFullNameFilter(name: string) {
  try {
    sessionStorage.setItem(SHARED_NAME_KEY, name)
  } catch {
    /* ignore */
  }
}

export function getSharedMonthFilter() {
  try {
    return sessionStorage.getItem(SHARED_MONTH_KEY) ?? ""
  } catch {
    return ""
  }
}

export function setSharedMonthFilter(month: string) {
  try {
    sessionStorage.setItem(SHARED_MONTH_KEY, month)
  } catch {
    /* ignore */
  }
}

export function getSharedYearFilter() {
  try {
    return sessionStorage.getItem(SHARED_YEAR_KEY) ?? ""
  } catch {
    return ""
  }
}

export function setSharedYearFilter(year: string) {
  try {
    sessionStorage.setItem(SHARED_YEAR_KEY, year)
  } catch {
    /* ignore */
  }
}

export function clearSharedEvoFilters() {
  setSharedFullNameFilter("")
  setSharedMonthFilter("")
  setSharedYearFilter("")
}

/** Default list filters: all months and all years. */
export function defaultEvoFilters(): EvoListFilters {
  return {
    fullName: "",
    month: "",
    year: "",
  }
}

/** Omit empty filter fields so API query strings never send `fullName=undefined`. */
export function buildEvoQueryParams(
  pagination: Record<string, unknown>,
  filters: EvoListFilters
) {
  const q: Record<string, unknown> = { ...pagination }
  const name = filters.fullName?.trim()
  const month = filters.month?.trim()
  const year = filters.year?.trim()
  if (name) q.fullName = name
  if (month) q.month = month
  if (year) q.year = year
  return q
}

/** DB tripStatus Paid/Not-Paid → UI Completed/Incomplete */
export function completionLabel(status?: string) {
  return status === "Paid" ? "Completed" : "Incomplete"
}

export function completionToDb(completed: boolean) {
  return completed ? "Paid" : "Not-Paid"
}

export function isCompleted(status?: string) {
  return status === "Paid"
}

/** Reviewed Yes/No ↔ expenseSubmissionOpenOrClose */
export function reviewedFromSubmission(v?: string) {
  return v === "Submitted" ? "Yes" : "No"
}

export function submissionFromReviewed(reviewed: string) {
  return reviewed === "Yes" ? "Submitted" : "Open"
}

export function formatAmountSubmitted(value: unknown) {
  if (value == null || value === "") {
    return { text: "Incomplete", incomplete: true }
  }
  const n = typeof value === "number" ? value : parseFloat(String(value))
  if (Number.isNaN(n)) {
    return { text: "Incomplete", incomplete: true }
  }
  return { text: `$${n.toFixed(2)}`, incomplete: false }
}

export function formatMoney(value: unknown, fallback = "$0.00") {
  if (value == null || value === "") return fallback
  const n = typeof value === "number" ? value : parseFloat(String(value))
  if (Number.isNaN(n)) return fallback
  return `$${n.toFixed(2)}`
}

export function submissionDateDisplay(date: unknown) {
  if (!date) return "—"
  return formatDate(String(date))
}

export function toDateInputValue(date: unknown) {
  if (!date) return ""
  const d = new Date(String(date))
  if (Number.isNaN(d.getTime())) return ""
  return d.toISOString().slice(0, 10)
}

export function approvedAmountColorClass(submitted: boolean) {
  return submitted
    ? "font-mono tabular-nums text-emerald-600 font-semibold"
    : "font-mono tabular-nums text-red-600 font-semibold"
}

export function isSubmittedRecord(record: {
  submissionDate?: unknown
  dateSubmitted?: unknown
  expenseSubmissionOpenOrClose?: string
  operationSubmissionOpenOrClose?: string
}) {
  if (record.submissionDate || record.dateSubmitted) return true
  const status =
    record.expenseSubmissionOpenOrClose || record.operationSubmissionOpenOrClose
  return status === "Submitted"
}

export async function downloadFileUrl(url: string, fileName: string) {
  try {
    const res = await fetch(url)
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = objectUrl
    link.download = fileName || "download"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(objectUrl)
  } catch {
    const link = document.createElement("a")
    link.href = url
    link.download = fileName || "download"
    link.target = "_blank"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}
